/**
 * Sessions grouped by work state (status.md, "Work states"): what needs you, what asked you a
 * question (ADR 0056: nothing held, quiet), what is working, what failed (ADR 0059: failures
 * never outrank live work; every failure sits under Failed, so Ended holds only finished work),
 * what is idle, and what ended, in that order. Pure, so the grouping is tested.
 */
import { ownWorkOf } from '@orgus/protocol';
import type { NeedsYouSessionLevel, Session } from '@orgus/protocol';
import { sessionOutcome } from './outcome.ts';
import type { WorkState } from '@orgus/ui';
import { FAILED_RECENT_MS, foldFailures } from './failures.ts';
import type { FailureFold } from './failures.ts';
import { compareSessions } from './labels.ts';

export type SessionGroupKey = 'needs-you' | 'asked' | 'failed' | 'working' | 'own-work' | 'idle' | 'ended';

export interface SessionGroupMeta {
  key: SessionGroupKey;
  /** The work state the group draws with. Idle and ended are both quiet (stale). */
  state: WorkState;
  label: string;
}

export const SESSION_GROUPS: readonly SessionGroupMeta[] = [
  { key: 'needs-you', state: 'needs-you', label: 'Needs you' },
  { key: 'asked', state: 'stale', label: 'Asked' },
  { key: 'working', state: 'running', label: 'Working' },
  // ORGU-132: its turn ended; its own tests, agent or monitor run and wake it (no turn in progress).
  { key: 'own-work', state: 'queued', label: 'Waiting on own work' },
  { key: 'failed', state: 'failed', label: 'Failed' },
  { key: 'idle', state: 'stale', label: 'Idle' },
  { key: 'ended', state: 'stale', label: 'Ended' },
];

export const SESSION_GROUP_META: Record<SessionGroupKey, SessionGroupMeta> = Object.fromEntries(SESSION_GROUPS.map((g) => [g.key, g])) as Record<SessionGroupKey, SessionGroupMeta>;

type GroupInput = Pick<Session, 'status' | 'turns' | 'toolCalls' | 'lastText' | 'state' | 'lastActivity'> & Partial<Pick<Session, 'ext'>>;

/** Each waiting session's place in Needs you (the needsYou selector), by session id. */
export type SessionLevels = ReadonlyMap<string, NeedsYouSessionLevel>;

/**
 * The group a session sits in: its live status, and for an ended one whether it failed. Every
 * failure sits under Failed whatever its age, so a failed row never hides among finished ones;
 * a Failed group with nothing from the last 24 hours folds away instead (startsCollapsed). A
 * waiting session whose turn ended on a question sits in Asked; one whose question is over a day
 * old, or dismissed or snoozed, sits in Idle (`level`, from the needsYou selector).
 */
export function sessionGroupOf(s: GroupInput, level?: NeedsYouSessionLevel | null): SessionGroupKey {
  if (s.status === 'waiting') return level === 'asked' ? 'asked' : level === 'open' || level === 'set-aside' ? 'idle' : 'needs-you';
  if (s.status === 'working') return ownWorkOf(s) ? 'own-work' : 'working';
  if (s.status === 'idle') return 'idle';
  // How it came out (outcome.ts): a failure a later turn recovered from is finished work.
  return sessionOutcome(s) === 'failed' ? 'failed' : 'ended';
}

export interface SessionStateGroup {
  key: SessionGroupKey;
  meta: SessionGroupMeta;
  sessions: Session[];
  /** Failed only: runs with the same cause and source folded into one row each (ADR 0059 decision 2). */
  folds?: FailureFold[];
  /** Failed only: how many failed in the last 24 hours (`now`); with none, the group folds away. */
  recent?: number;
}

/** Non-empty groups in the order of attention; inside each, what needs you first, then the most recent. */
export function groupByState(list: readonly Session[], levels?: SessionLevels, now?: number): SessionStateGroup[] {
  const by = new Map<SessionGroupKey, Session[]>();
  for (const s of list) {
    const k = sessionGroupOf(s, levels?.get(s.id));
    const arr = by.get(k);
    if (arr) arr.push(s);
    else by.set(k, [s]);
  }
  return SESSION_GROUPS.filter((g) => by.has(g.key)).map((g) => {
    const sessions = [...by.get(g.key)!].sort(compareSessions);
    if (g.key !== 'failed') return { key: g.key, meta: g, sessions };
    // The rows in fold order, so j and k and the drawer walk them as shown.
    const folds = foldFailures(sessions);
    const recent = now === undefined ? sessions.length : sessions.filter((s) => now - s.lastActivity < FAILED_RECENT_MS).length;
    return { key: g.key, meta: g, sessions: folds.flatMap((f) => f.runs), folds, recent };
  });
}

/**
 * Ended work, and failures none of which is from the last 24 hours (history, ADR 0059 decision
 * 3), fold away while something is live, so the first screen is what is happening now. With
 * nothing live, or while a search or a status filter narrows the list, every group opens.
 */
export function startsCollapsed(key: SessionGroupKey, groups: readonly SessionStateGroup[], narrowed: boolean): boolean {
  if (narrowed) return false;
  if (key === 'failed' && groups.find((g) => g.key === 'failed')?.recent !== 0) return false;
  if (key !== 'ended' && key !== 'failed') return false;
  return groups.some((g) => g.key !== 'ended' && g.key !== 'failed');
}

/** Waits shorter than this read as no wait at all: "oldest 0ms" says nothing. */
export const MIN_WAIT_MS = 60_000;

/**
 * The longest current wait among what blocks, in ms: from each blocked ask's start (`since`, the
 * needsYou selector's blocked items by session) or else the session's last activity. 0 when
 * nothing has waited a minute (MIN_WAIT_MS), so a figure is only shown when it means something.
 */
export function oldestWait(list: readonly Pick<Session, 'id' | 'status' | 'lastActivity'>[], now: number, since?: ReadonlyMap<string, number>): number {
  let oldest = 0;
  for (const s of list) if (s.status === 'waiting') oldest = Math.max(oldest, now - (since?.get(s.id) ?? s.lastActivity));
  return oldest >= MIN_WAIT_MS ? oldest : 0;
}

/** When each blocked session's wait began (the needsYou selector's blocked items), by session id. */
export function blockedSince(items: readonly { sessionId: string | null; at: number }[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const i of items) if (i.sessionId) out.set(i.sessionId, Math.min(out.get(i.sessionId) ?? Infinity, i.at));
  return out;
}

/** Subagents and workflows running across sessions. */
export function runningAgents(list: readonly Pick<Session, 'subagents' | 'workflows'>[]): { subagents: number; workflows: number } {
  let subagents = 0;
  let workflows = 0;
  for (const s of list) {
    subagents += s.subagents.filter((a) => a.status === 'running').length;
    workflows += s.workflows.filter((w) => w.status === 'running').length;
  }
  return { subagents, workflows };
}
